import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuthStore } from '@/store/authStore';
import { useAcademicFeedback } from '@/hooks/useAcademicFeedback';
import { useAcademicRenewalFormState } from '@/components/Academic/hooks/useAcademicRenewalFormState';
import AcademicRenewalFormFields from '@/components/Academic/AcademicRenewalFormFields';
import FeedbackSurveyLayout from './FeedbackSurveyLayout';
import { validateContactEmail } from '@/components/Support/ValidationLogic';

const RENEWAL_TOTAL_STEPS = 6;

const ACADEMIC_API =
  process.env.REACT_APP_ACADEMIC_API_URL || 'https://o0vnudbmyc.execute-api.eu-central-1.amazonaws.com';

function decodeUrlSafeBase64(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + '='.repeat(paddingLength);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function getEmailFromRenewToken(rt: string | null) {
  if (!rt) return null;
  try {
    const decoded = decodeUrlSafeBase64(rt).trim();
    if (!decoded) return null;

    if (decoded.includes('@')) {
      const normalizedEmail = decoded.toLowerCase();
      return validateContactEmail(normalizedEmail) ? normalizedEmail : null;
    }

    const parsed = JSON.parse(decoded);
    const tokenEmail = typeof parsed?.email === 'string' ? parsed.email.trim().toLowerCase() : '';
    return tokenEmail && validateContactEmail(tokenEmail) ? tokenEmail : null;
  } catch {
    return null;
  }
}

const RenewalFeedbackSurvey: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rt = searchParams.get('rt');

  const user = useAuthStore(s => s.user);
  const { submitRenewalFeedback, isSubmitting, feedbackError } = useAcademicFeedback();
  const { formData, errors, t, validateStep, validateForm, handleInputChange, setMultiSelect } =
    useAcademicRenewalFormState();

  const [step, setStep] = useState(0);
  const [tokenEmail, setTokenEmail] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(() => Boolean(rt));
  const [isVerifiedRenewalToken, setIsVerifiedRenewalToken] = useState(false);
  const [manualEmail, setManualEmail] = useState('');

  useEffect(() => {
    if (!rt) {
      setTokenEmail(null);
      setTokenLoading(false);
      setIsVerifiedRenewalToken(false);
      return;
    }

    let cancelled = false;
    setTokenLoading(true);

    fetch(`${ACADEMIC_API}/verify-renew-survey-token?rt=${encodeURIComponent(rt)}`)
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        return { ok: r.ok, data };
      })
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (ok && data?.email && typeof data.email === 'string') {
          const verifiedEmail = String(data.email).trim().toLowerCase();
          setTokenEmail(verifiedEmail);
          setIsVerifiedRenewalToken(true);
          setManualEmail(prev => (prev ? prev : verifiedEmail));
          return;
        }

        setIsVerifiedRenewalToken(false);
        const fallbackEmail = getEmailFromRenewToken(rt);
        if (fallbackEmail) {
          setTokenEmail(fallbackEmail);
          setManualEmail(prev => (prev ? prev : fallbackEmail));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setIsVerifiedRenewalToken(false);
        const fallbackEmail = getEmailFromRenewToken(rt);
        if (fallbackEmail) {
          setTokenEmail(fallbackEmail);
          setManualEmail(prev => (prev ? prev : fallbackEmail));
        } else {
          setTokenEmail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTokenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rt]);

  const resolvedEmail = useMemo(() => {
    const u = user?.email?.trim().toLowerCase();
    if (u) return u;
    if (tokenEmail) return tokenEmail;
    const m = manualEmail.trim();
    if (m && validateContactEmail(m)) return m;
    return null;
  }, [user, tokenEmail, manualEmail]);

  const canProceed = Boolean(user) || (!tokenLoading && Boolean(resolvedEmail));

  const buildUserInfo = useCallback(() => {
    if (!resolvedEmail) return null;
    const groups = user?.['cognito:groups'];
    const plan = Array.isArray(groups) ? groups.join(',') : groups ? String(groups) : 'academic';
    return {
      name: user?.['custom:name'] || user?.name || user?.given_name || user?.family_name || 'Academic User',
      email: resolvedEmail,
      organization:
        user?.['custom:company'] || user?.['custom:organization'] || user?.organization || 'Not specified',
      plan,
    };
  }, [user, resolvedEmail]);

  const submit = useCallback(async () => {
    const userInfo = buildUserInfo();
    if (!userInfo) {
      await Swal.fire({
        title: 'Email required',
        text: 'Please enter the email address of your DISGENET account, or open this survey from the link we sent you.',
        icon: 'info',
        confirmButtonColor: '#0d9488',
      });
      return;
    }

    if (!validateForm()) return;

    const ok = await submitRenewalFeedback(formData, userInfo, {
      renewalSurveyToken: isVerifiedRenewalToken ? rt || undefined : undefined,
      reactivateDisabledAccount: !user,
    });
    if (ok) {
      await Swal.fire({
        title: 'Thank you',
        text: 'Your feedback has been sent successfully. If a renewal was started, please check your email to confirm.',
        icon: 'success',
        confirmButtonColor: '#0d9488',
      });
      navigate('/');
    }
  }, [
    buildUserInfo,
    formData,
    isVerifiedRenewalToken,
    navigate,
    rt,
    submitRenewalFeedback,
    user,
    validateForm,
  ]);

  const handlePrimary = async () => {
    if (!canProceed) return;
    if (!validateStep(step)) return;

    if (step === 4) {
      if (formData.suggestions?.trim()) {
        setStep(5);
        return;
      }
      await submit();
      return;
    }

    if (step === 5) {
      await submit();
      return;
    }

    if (step < RENEWAL_TOTAL_STEPS - 1) {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const primaryLabel =
    step === 4 && !formData.suggestions?.trim() ? t.btnSubmit : step === 5 ? t.btnSubmit : 'Next';

  const accountEmail = useMemo(() => {
    const userEmail = user?.email?.trim().toLowerCase();
    if (userEmail) return userEmail;
    if (tokenEmail) return tokenEmail;
    const typedEmail = manualEmail.trim().toLowerCase();
    return typedEmail || null;
  }, [manualEmail, tokenEmail, user]);

  const showAccountInfo = !tokenLoading && Boolean(accountEmail);
  const showManualEmailInput = !tokenLoading && !accountEmail;

  return (
    <FeedbackSurveyLayout
      title={t.title}
      subtitle={t.description}
      currentIndex={step}
      totalSteps={RENEWAL_TOTAL_STEPS}
      onBack={handleBack}
      onPrimary={handlePrimary}
      backDisabled={step === 0}
      primaryLabel={primaryLabel}
      primaryLoading={isSubmitting}
      primaryDisabled={isSubmitting || !canProceed}
    >
      {feedbackError && (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
          {feedbackError}
        </p>
      )}

      <div className="mb-6 rounded-xl border border-teal-100 bg-teal-50/50 px-4 py-3">
        {tokenLoading && <p className="text-sm text-gray-600">Verifying your renewal link...</p>}
        {showAccountInfo && (
          <p className="text-sm text-gray-800">
            <span className="font-medium">Account:</span> {accountEmail}
          </p>
        )}
        {showManualEmailInput && (
          <div className="mt-2">
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-600">
              Email (your DISGENET account) *
            </label>
            <input
              type="email"
              className="form-input w-full max-w-md rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              value={manualEmail}
              onChange={e => setManualEmail(e.target.value)}
              placeholder="name@institution.edu"
              autoComplete="email"
            />
          </div>
        )}
      </div>

      <AcademicRenewalFormFields
        mode="step"
        stepIndex={step}
        formData={formData}
        errors={errors}
        t={t}
        accent="teal"
        handleInputChange={handleInputChange}
        setMultiSelect={setMultiSelect}
      />
    </FeedbackSurveyLayout>
  );
};

export default RenewalFeedbackSurvey;
